import React, { useState } from 'react';

import {
  CallScribeDraftOutcome,
  patchCallScribeDraft,
} from 'services/callScribe';
import DurationStepper from 'components/CallScribe/DurationStepper';
import usePaperStyles from 'components/CallScribe/paperStyles';

export const SAVED_ON_TICKET = 'Updated on the ticket.';
export const SAVE_FAILED = 'The change could not be saved. Nothing on the ticket was changed.';

export interface IPostedTimeEntryEditorProps {
  callId: number;
  initialMinutes: number;
  buttonClassName?: string;
  statusClassName?: string;
  errorClassName?: string;
}

type TSave =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; message: string }
  | { kind: 'failed'; message: string };

const PostedTimeEntryEditor: React.FC<IPostedTimeEntryEditorProps> = ({
  callId,
  initialMinutes,
  buttonClassName,
  statusClassName,
  errorClassName,
}) => {
  const paper = usePaperStyles();
  const [minutes, setMinutes] = useState<number | undefined>(undefined);
  const [savedMinutes, setSavedMinutes] = useState<number | undefined>(undefined);
  const [save, setSave] = useState<TSave>({ kind: 'idle' });

  const baseline = savedMinutes ?? initialMinutes;
  const minutesValue = minutes ?? baseline;
  const dirty = minutes !== undefined && minutes !== baseline;
  const saving = save.kind === 'saving';

  const onSave = async (): Promise<void> => {
    const sent = minutesValue;
    setSave({ kind: 'saving' });
    try {
      const response = await patchCallScribeDraft(callId, { durationMinutes: sent });
      const outcome = response?.data?.outcome;
      const message = response?.data?.message;
      if (outcome === CallScribeDraftOutcome.SavedAndUpdatedTicket) {
        setSavedMinutes(sent);
        setMinutes(undefined);
        setSave({ kind: 'saved', message: SAVED_ON_TICKET });
      } else if (outcome === CallScribeDraftOutcome.SavedLocally ||
        outcome === CallScribeDraftOutcome.SavedButTicketUpdateUnsupported) {
        setSavedMinutes(sent);
        setMinutes(undefined);
        setSave({ kind: 'saved', message: message ?? 'Saved, but the ticket still shows the original entry.' });
      } else {
        setSave({ kind: 'failed', message: message ?? SAVE_FAILED });
      }
    } catch {
      setSave({ kind: 'failed', message: SAVE_FAILED });
    }
  };

  return (
    <>
      <div className={paper.fieldGrid}>
        <DurationStepper
          id={`posted-${callId}-duration`}
          value={minutesValue}
          onChange={setMinutes}
          inputLabel="Duration (minutes)"
        />
      </div>
      <button
        type="button"
        className={[paper.textButton, buttonClassName].filter(Boolean).join(' ')}
        onClick={() => { void onSave(); }}
        disabled={!dirty || saving || minutesValue <= 0}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </button>
      {save.kind === 'saved' ? (
        <span className={[paper.small, statusClassName].filter(Boolean).join(' ')} role="status">{save.message}</span>
      ) : null}
      {save.kind === 'failed' ? (
        <div className={`${paper.notice} ${paper.noticeBad} ${errorClassName ?? ''}`} role="alert">
          <p className={paper.noticeBody}>{save.message}</p>
        </div>
      ) : null}
    </>
  );
};

export default PostedTimeEntryEditor;
